import { EventEmitter } from '@timing71/common';
import { API_ROOT, CONFIG_UPDATE, RACE_CONTROL_MESSAGE, SESSION_UPDATE } from './constants.js';

const POLL_INTERVAL = 2000; // ms

export class SessionMonitor extends EventEmitter {

  constructor(connectionService, series, season, event, session) {
    super();
    this.connectionService = connectionService;

    this.ssid = {
      cs_id: series,
      season: season,
      event_id: event.event_id,
      session_id: session.session_id
    };

    this.event = event;
    this.session = session;

    this._data_id = 0;
    this._message_id = 0;
    this._cars = {};

    this.poll = this.poll.bind(this);
    this._post = this._post.bind(this);
    this.start = this.start.bind(this);
    this.stop = this.stop.bind(this);
  }

  start() {
    this._getConfig().then(
      cfg => {
        this.emit(CONFIG_UPDATE, cfg);
        this.poll();
      }
    );
  }

  stop() {
    if (this._timeout) {
      clearTimeout(this._timeout);
      this._timeout = null;
    }
  }

  async poll() {
    try {
      const lastMsg = await this._getLastMessage();
      if (lastMsg && lastMsg.data_id > this._message_id) {
        this.emit(RACE_CONTROL_MESSAGE, lastMsg);
        this._message_id = lastMsg.data_id;
      }

      const ranking = await this._getRanking();
      const raceInfo = await this._getRaceInfo();

      this.emit(
        SESSION_UPDATE,
        {
          event: this.event,
          session: this.session,
          ranking,
          raceInfo
        }
      );
    }
    catch {
      // If at first you don't succeed, poll, poll again...
      // (Sometimes ITS LIVE returns truncated JSON. It'll be back to normal
      // the next time we ask for it, most likely.)
    }
    finally {
      this._timeout = setTimeout(this.poll, POLL_INTERVAL);
    }
  }

  _post(url, payload) {
    return this.connectionService.fetch(
      url,
      {
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json'
        },
        method: 'POST',
      }
    )
  }

  async _getRanking() {
    const ranking = await this._post(
      `${API_ROOT}/Session/GetRankingWithBestOfAll`,
      {
        startId: this._data_id,
        startPos: 0,
        endPos: 1000000,
        ssid: this.ssid
      }
    );

    const rankingJSON = JSON.parse(ranking);

    const maxDataId = Math.max(this._data_id, ...rankingJSON.ranking.map(r => r.data_id));
    this._data_id = maxDataId;

    rankingJSON.ranking.forEach(
      car => {
        this._cars[car.competitor_id] = car;
      }
    );

    return {
      ...rankingJSON,
      ranking: Object.values(this._cars).sort((a, b) => (a.pos || 99999) - (b.pos || 99999))
    };
  }

  async _getRaceInfo() {
    const ri = await this._post(
      `${API_ROOT}/Session/GetLastRaceInfo`,
      this.ssid
    );

    return JSON.parse(ri);
  }

  async _getLastMessage() {
    const msg = await this._post(
      `${API_ROOT}/Session/GetLastMsg`,
      this.ssid
    );

    if (msg.length > 0) {
      return JSON.parse(msg);
    }

    return null;
  }

  async _getConfig() {
    const cfg = await this._post(
      `${API_ROOT}/Session/GetCfg`,
      this.ssid
    );

    return JSON.parse(cfg);
  }

}
